import { Avatar, AvatarFallback } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import type { User } from '@/types';
import { Link, router } from '@inertiajs/react';
import { AvatarImage } from '@radix-ui/react-avatar';
import { Building2, Mail } from 'lucide-react';

interface EmployeeTableProps {
    users: User[];
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
    onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
    onColumnHide?: (column: string) => void;
    onColumnShow?: (column: string) => void;
    visibleColumns?: Set<string>;
    columnFilters?: Record<string, string[]>;
    columnFilterOptions?: Record<string, Array<{ value: string; label: string }>>;
    onFilterChange?: (column: string, values: string[]) => void;
    selectionMode?: boolean;
    selectedIds?: Set<string>;
    onToggleSelect?: (id: string) => void;
    onToggleSelectAll?: () => void;
    currentUserId?: string;
}

const DEFAULT_COLUMNS = ['fullName', 'position', 'role', 'email', 'companies', 'status'];

export function EmployeeTable({
    users,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
    visibleColumns = new Set(DEFAULT_COLUMNS),
    columnFilters = {},
    columnFilterOptions = {},
    onFilterChange,
    selectionMode = false,
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
    currentUserId,
}: EmployeeTableProps) {
    const { t } = useTranslations();

    const columns = [
        { id: 'fullName', label: t('employees.tables.colleague', 'Colleague'), visible: visibleColumns.has('fullName') },
        { id: 'position', label: t('employees.tables.position', 'Position'), visible: visibleColumns.has('position') },
        { id: 'role', label: t('employees.tables.role', 'Role'), visible: visibleColumns.has('role') },
        { id: 'email', label: t('employees.tables.email', 'Email'), visible: visibleColumns.has('email') },
        { id: 'companies', label: t('employees.tables.companies', 'Companies'), visible: visibleColumns.has('companies') },
        { id: 'status', label: t('employees.tables.status', 'Status'), visible: visibleColumns.has('status') },
    ].filter((column) => column.visible);

    const selectableUsers = users.filter((user) => user.id !== currentUserId);
    const allSelected = selectionMode && selectableUsers.length > 0 && selectableUsers.every((user) => selectedIds?.has(String(user.id)));
    const someSelected = selectionMode && !allSelected && selectableUsers.some((user) => selectedIds?.has(String(user.id)));

    return (
        <div className="space-y-4">
            <div className="bg-card mb-4 items-center justify-between gap-3 rounded-xl border px-4 py-2.5">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {selectionMode && (
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                        onCheckedChange={() => onToggleSelectAll?.()}
                                        aria-label={t('employees.tables.select_all', 'Select all')}
                                    />
                                </TableHead>
                            )}
                            {columns.map((column) => {
                                const selectedFilters = columnFilters[column.id] || [];
                                return (
                                    <TableHead key={column.id}>
                                        <TableColumnHeader
                                            label={column.label}
                                            sortDirection={sortColumn === column.id ? sortDirection : null}
                                            onSortAscending={() => onSortChange?.(column.id, 'asc')}
                                            onSortDescending={() => onSortChange?.(column.id, 'desc')}
                                            onHide={() => onColumnHide?.(column.id)}
                                            filterOptions={columnFilterOptions[column.id] || []}
                                            selectedFilters={selectedFilters}
                                            onFilterChange={(values) => onFilterChange?.(column.id, values)}
                                            isFiltered={selectedFilters.length > 0}
                                        />
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + (selectionMode ? 1 : 0)} className="text-muted-foreground py-8 text-center">
                                    {t('employees.index.empty.no_employees_found', 'No employees found.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => {
                                const isSelected = selectionMode && selectedIds?.has(String(user.id));
                                const isSelf = currentUserId !== undefined && user.id === currentUserId;
                                return (
                                    <TableRow
                                        key={user.id}
                                        data-state={isSelected ? 'selected' : undefined}
                                        className={cn(
                                            'hover:bg-muted/50 transition-all',
                                            selectionMode ? 'cursor-default' : 'cursor-pointer',
                                            isSelected && 'bg-muted/40',
                                        )}
                                        onClick={() => {
                                            if (selectionMode) {
                                                if (!isSelf) onToggleSelect?.(String(user.id));
                                                return;
                                            }
                                            router.visit(route('employees.show', user.id));
                                        }}
                                    >
                                        {selectionMode && (
                                            <TableCell className="w-10" onClick={(event) => event.stopPropagation()}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => onToggleSelect?.(String(user.id))}
                                                    disabled={isSelf}
                                                    aria-label={t('employees.tables.select_row', 'Select row')}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column) => {
                                            if (column.id === 'fullName') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <Link
                                                            href={route('employees.show', user.id)}
                                                            onClick={(event) => event.stopPropagation()}
                                                            className="text-foreground hover:bg-accent flex w-full items-center gap-3 rounded px-2 py-1.5 text-left font-semibold transition-colors"
                                                        >
                                                            <Avatar className="size-7">
                                                                <AvatarImage src={user.avatar} className="rounded-2xl" />
                                                                <AvatarFallback className="rounded-2xl">
                                                                    {user.first_name.charAt(0)}
                                                                    {user.last_name.charAt(0)}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <span className="inline-flex items-center gap-1.5">
                                                                {user.first_name} {user.last_name}
                                                                {user.azure_id && (
                                                                    <span
                                                                        aria-label="Microsoft 365"
                                                                        title={t('employees.tables.m365_badge', 'Microsoft 365 user')}
                                                                        className="inline-flex shrink-0"
                                                                    >
                                                                        <svg
                                                                            width="12"
                                                                            height="12"
                                                                            viewBox="0 0 23 23"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path fill="#F25022" d="M1 1h10v10H1z" />
                                                                            <path fill="#7FBA00" d="M12 1h10v10H12z" />
                                                                            <path fill="#00A4EF" d="M1 12h10v10H1z" />
                                                                            <path fill="#FFB900" d="M12 12h10v10H12z" />
                                                                        </svg>
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </Link>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'position') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <span className="text-muted-foreground">{user.position || '—'}</span>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'role') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        {user.role || t('employees.tables.no_role', 'No role assigned')}
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'email') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <a
                                                            href={`mailto:${user.email}`}
                                                            className="flex items-center gap-2"
                                                            onClick={(event) => event.stopPropagation()}
                                                        >
                                                            <Mail className="text-muted-foreground size-4" />
                                                            <span>{user.email}</span>
                                                        </a>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'status') {
                                                const suspended = Boolean(user.suspended_at);
                                                return (
                                                    <TableCell key={column.id}>
                                                        <Badge variant={suspended ? 'destructive' : 'secondary'}>
                                                            {suspended
                                                                ? t('employees.tables.status_suspended', 'Suspended')
                                                                : t('employees.tables.status_active', 'Active')}
                                                        </Badge>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'companies') {
                                                const companies = user.companies ?? [];
                                                const displayedCompanies = companies.slice(0, 2);
                                                const remainingCount = companies.length - displayedCompanies.length;

                                                return (
                                                    <TableCell key={column.id}>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            {displayedCompanies.map((company) => (
                                                                <Link
                                                                    key={company.id}
                                                                    href={`/companies/${company.id}`}
                                                                    className="flex items-center gap-2 hover:underline"
                                                                    onClick={(event) => event.stopPropagation()}
                                                                >
                                                                    <Building2 className="text-muted-foreground size-4" />
                                                                    <span>{company.name}</span>
                                                                </Link>
                                                            ))}
                                                            {remainingCount > 0 && (
                                                                <span className="rounded-md border px-2 py-0.5 text-xs font-semibold">
                                                                    +{remainingCount}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                );
                                            }

                                            return null;
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
